/**
 * SunoCoverJobsContext — фоновые задачи генерации обложек + picker-state.
 *
 * По структуре аналогичен `SunoWavJobsContext`, но отличается:
 *   - Результат — ДВА URL картинок, не один файл.
 *   - После завершения не качаем файл, а показываем юзеру picker «выбери какую».
 *   - Применение = обновление `suno_tracks.image_url` через API, а не download.
 *
 * Состояния job'а:
 *   1. Запущен → polling пока не придёт 2 URL
 *   2. 2 URL получены → вместе с title кладём в `pendingChoices` → родитель
 *      открывает picker-sheet
 *   3. Юзер выбрал → setTrackCover API → onCompleted refresh → переменная
 *      сбрасывается
 *
 * Persistent: in-flight jobs и pendingChoices сохраняются в localStorage.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { generateCover, getUserTracks, setTrackCover, type SunoTrack } from '../api/music'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'

const LS_PENDING_KEY = 'suno_cover_pending_v1'
const LS_CHOICES_KEY = 'suno_cover_choices_v1'
const POLL_INTERVAL_MS = 10_000

interface PendingCoverJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

export interface CoverChoice {
	trackId: string
	title: string | null
	/** URL-ы вариантов (обычно 2). Юзер выбирает один. */
	imageUrls: string[]
}

interface SunoCoverJobsValue {
	start: (track: SunoTrack) => void
	isRunning: (trackId: string) => boolean
	/** Текущее состояние ожидающего выбора: если job завершился и вернул URL'ы —
	 *  здесь они для показа в picker'е. */
	pendingChoice: CoverChoice | null
	/** Явно закрыть picker (отменить выбор) — URL'ы будут потеряны, трек
	 *  останется со старой обложкой. */
	dismissChoice: () => void
	/** Применить один из URL как image_url трека. Вызывает API + refresh. */
	applyChoice: (trackId: string, imageUrl: string) => Promise<void>
	/** Ручная проверка: ищем cover-треки в /music/tracks для этого source.
	 *  Если нашли — открываем picker без ожидания таймаута. */
	recheck: (trackId: string, title: string | null) => Promise<void>
}

const SunoCoverJobsContext = createContext<SunoCoverJobsValue | null>(null)

// ─── localStorage helpers ──────────────────────────────────────────
function loadPending(): Record<string, PendingCoverJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingCoverJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingCoverJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingCoverJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		/* quota — запуск просто не восстановится после перезагрузки */
	}
}
function loadChoice(): CoverChoice | null {
	try {
		const raw = localStorage.getItem(LS_CHOICES_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed === 'object' && Array.isArray(parsed.imageUrls) && parsed.imageUrls.length > 0) {
			return parsed
		}
		return null
	} catch {
		return null
	}
}
function saveChoice(choice: CoverChoice | null) {
	try {
		if (choice) localStorage.setItem(LS_CHOICES_KEY, JSON.stringify(choice))
		else localStorage.removeItem(LS_CHOICES_KEY)
	} catch {
		/* quota — выбор обложки не переживёт перезагрузку */
	}
}

interface ProviderProps {
	children: ReactNode
	/** Вызывается после успешного применения обложки — чтобы родитель мог
	 *  обновить список треков (Library) и увидеть новую image_url. */
	onCoverApplied?: () => void
}

export function SunoCoverJobsProvider({ children, onCoverApplied }: ProviderProps) {
	const [pendingChoice, setPendingChoice] = useState<CoverChoice | null>(() => loadChoice())
	const [runningIds, setRunningIds] = useState<Set<string>>(() => new Set(Object.keys(loadPending())))
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingCoverJob>>(loadPending())
	const onCoverAppliedRef = useRef(onCoverApplied)
	useEffect(() => { onCoverAppliedRef.current = onCoverApplied }, [onCoverApplied])

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingCoverJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	const publishChoice = useCallback((choice: CoverChoice | null) => {
		setPendingChoice(choice)
		saveChoice(choice)
	}, [])

	// ─── Core polling loop ────────────────────────────────────────
	const runPolling = useCallback(
		async (trackId: string, requestId: string, trackTitle: string | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: trackTitle,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			const startedAt = pendingRef.current[trackId]?.startedAt ?? Date.now()
			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, { minDelayMs: POLL_INTERVAL_MS, startedAt })
				if (!s) return
				if (s.status === 'failed') {
					showToast(s.error_message_ru || s.error_message || 'Обложка не сгенерировалась', 'error')
					return
				}
				let imageUrls: string[] = s.result_urls || (s.result_url ? [s.result_url] : [])

				// Готово, а ссылок в ответе нет: worker мог сохранить обложки отдельными записями с
				// operation='music_cover' и source_track_id нашего трека — берём их оттуда.
				if (imageUrls.length === 0) {
					try {
						const { tracks } = await getUserTracks({
							operation: 'music_cover',
							limit: 20,
						})
						// Ищем cover-треки для нашего source, созданные после старта job'а.
						const relevant = tracks.filter((t) => {
							if (t.source_track_id !== trackId) return false
							if (!t.image_url) return false
							if (t.created_at && Date.parse(t.created_at) < startedAt - 60_000) {
								// старее чем минута до старта — это не наш job
								return false
							}
							return true
						})
						if (relevant.length > 0) {
							imageUrls = relevant
								.map((t) => t.image_url)
								.filter((u): u is string => typeof u === 'string' && u.length > 0)
						}
					} catch {
						// ignore
					}
				}

				if (imageUrls.length === 0) {
					showToast(
						'Suno завершил задачу без обложек — попробуй ещё раз или загрузи свою обложку через «Загрузить свою обложку».',
						'error',
					)
					return
				}

				// Публикуем choice — родитель покажет picker-sheet.
				publishChoice({ trackId, title: trackTitle, imageUrls })
				showToast('Обложки готовы — выбери вариант', 'success')
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, publishChoice, setPendingJob],
	)

	// ─── start(): внешний API ─────────────────────────────────────
	const start = useCallback(
		async (track: SunoTrack) => {
			const trackId = track.id

			if (activeRef.current.has(trackId)) {
				showToast('Генерация обложки уже идёт...', 'info')
				return
			}

			showToast(`Генерируем обложку для «${track.title?.trim() || 'трека'}»...`, 'info')

			const res = await generateCover({ track_id: trackId })

			// Recovery от backend'а: Suno сказал «already generated» и бэк
			// вытащил URL'ы через record-info. Сразу публикуем choice без polling.
			if (res.ok && Array.isArray(res.cover_urls) && res.cover_urls.length > 0) {
				publishChoice({ trackId, title: track.title, imageUrls: res.cover_urls })
				showToast('Обложки уже готовы — выбери вариант', 'success')
				return
			}

			// Frontend-recovery для «already generated» / «already exists»:
			// Suno генерил обложку раньше, но наш бэк URL не сохранил. Пробуем
			// дёрнуть повторно с polling'ом — в надежде что worker/record-info
			// восстановят URL'ы.
			if (
				!res.ok
				&& res.error
				&& (res.error.toLowerCase().includes('already generated')
					|| res.error.toLowerCase().includes('already exists'))
			) {
				showToast(
					'Suno уже создал обложки для этого трека, но наш сервер их не сохранил. Обратись в поддержку — исправим.',
					'error',
				)
				return
			}

			// Известный кейс Suno: инструментальные треки и SFX часто генерятся
			// без prompt'а — для них API обложек не работает. Показываем понятное
			// сообщение вместо английского Suno-техсообщения.
			if (
				!res.ok
				&& res.error
				&& res.error.toLowerCase().includes('prompt cannot be empty')
			) {
				showToast(
					'У этого трека нет промпта, поэтому Suno не может сгенерировать обложку. Загрузи свою обложку через «Загрузить обложку» (если доступно).',
					'error',
				)
				return
			}

			if (!res.ok || !res.request_id) {
				showToast(res.error || 'Ошибка запуска генерации обложки', 'error')
				return
			}

			runPolling(trackId, res.request_id, track.title)
		},
		[publishChoice, runPolling],
	)

	// ─── applyChoice: применить выбранный URL как image_url трека ─
	const applyChoice = useCallback(
		async (trackId: string, imageUrl: string) => {
			const res = await setTrackCover(trackId, imageUrl)
			if (!res.ok) {
				showToast(res.error || 'Не удалось применить обложку', 'error')
				return
			}
			showToast('Обложка обновлена', 'success')
			// Сбросить choice → закрыть picker
			publishChoice(null)
			// Сообщить родителю что нужно обновить список треков
			onCoverAppliedRef.current?.()
			// Глобальное событие — Library и другие экраны слушают и
			// перезагружают треки чтобы подхватить новый image_url.
			try {
				window.dispatchEvent(
					new CustomEvent('suno-cover-applied', { detail: { trackId, imageUrl } }),
				)
			} catch {
				/* событие необязательно — обложка уже сохранена */
			}
		},
		[publishChoice],
	)

	const dismissChoice = useCallback(() => {
		publishChoice(null)
	}, [publishChoice])

	// Ручная проверка — дёргается кнопкой «Проверить сейчас» в UI.
	// Ищем в БД свежие cover-треки для этого source. Если есть — открываем picker.
	const recheck = useCallback(
		async (trackId: string, title: string | null) => {
			try {
				const { tracks } = await getUserTracks({ operation: 'music_cover', limit: 20 })
				const urls = tracks
					.filter((t) => t.source_track_id === trackId && t.image_url)
					.map((t) => t.image_url!)
					.filter(Boolean)
				if (urls.length > 0) {
					publishChoice({ trackId, title, imageUrls: urls })
					showToast('Обложки найдены — выбери вариант', 'success')
				} else {
					showToast('Обложки ещё не готовы, попробуй позже', 'info')
				}
			} catch {
				showToast('Не удалось проверить статус', 'error')
			}
		},
		[publishChoice],
	)

	// ─── Restore pending jobs после reload ────────────────────────
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				runPolling(trackId, job.requestId, job.title)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// ─── Value ────────────────────────────────────────────────────
	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const value = useMemo<SunoCoverJobsValue>(
		() => ({ start, isRunning, pendingChoice, dismissChoice, applyChoice, recheck }),
		[start, isRunning, pendingChoice, dismissChoice, applyChoice, recheck],
	)

	return (
		<SunoCoverJobsContext.Provider value={value}>{children}</SunoCoverJobsContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoCoverJobs(): SunoCoverJobsValue {
	const ctx = useContext(SunoCoverJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			pendingChoice: null,
			dismissChoice: () => {},
			applyChoice: async () => {},
			recheck: async () => {},
		}
	}
	return ctx
}
